import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // A lifecycle event stamps its time at the start of the transition, and PaperTrail inserts
  // the version that caused it a moment later. Inside this window the save sorts first.
  static causeWindowMs = 2000

  static targets = [
    "event",
    "board",
    "yearTemplate",
    "yearButton",
    "sortButton",
    "sortLabel",
    "minorSwitch",
    "count",
    "source"
  ]

  connect() {
    this.sortDescending = true
    this.selectedYear = ""
    this.showMinor = false
    this.redraw()
  }

  toggleEvent(event) {
    const item = event.currentTarget.closest(".depozen-ui-timeline__item")
    if (!item) return

    this.setOpen(item, !item.classList.contains("is-open"))
  }

  toggleMinor(event) {
    this.showMinor = event.currentTarget.checked
    this.element.classList.toggle("is-showing-minor", this.showMinor)
    this.syncMinorInert()
  }

  filterYear(event) {
    this.selectedYear = event.currentTarget.dataset.year || ""
    // Same classes as DepozenUi::FilterPillComponent's active / inactive states.
    this.yearButtonTargets.forEach((button) => {
      const active = button.dataset.year === this.selectedYear
      button.classList.toggle("depozen-ui-filter-pill--active", active)
      button.classList.toggle("bg-primary", active)
      button.classList.toggle("text-white", active)
      button.classList.toggle("depozen-ui-filter-pill--inactive", !active)
      button.classList.toggle("text-dark", !active)
    })
    this.redraw()
  }

  toggleSort() {
    this.sortDescending = !this.sortDescending
    if (this.hasSortButtonTarget) this.sortButtonTarget.classList.toggle("is-oldest", !this.sortDescending)
    if (this.hasSortLabelTarget) {
      const label = this.sortDescending
        ? this.sortButtonTarget.dataset.labelNewest
        : this.sortButtonTarget.dataset.labelOldest
      if (label) this.sortLabelTarget.textContent = label
    }
    this.redraw()
  }

  setOpen(item, open) {
    const row = item.querySelector(".depozen-ui-timeline__row")
    if (!row || row.disabled) return

    item.classList.toggle("is-open", open)
    row.setAttribute("aria-expanded", open ? "true" : "false")
  }

  redraw() {
    if (!this.hasBoardTarget || !this.hasYearTemplateTarget) return

    if (this.hasSourceTarget) {
      this.eventTargets.forEach((item) => this.sourceTarget.append(item))
    }

    const year = this.selectedYear
    const yearItems = this.eventTargets.filter((item) => !year || item.dataset.year === year)
    yearItems.sort((a, b) => this.compareItems(a, b))
    // Minor rows stay in the rail. The switch only toggles `.is-showing-minor`, so their
    // height can ease open and shut instead of the list being rebuilt.
    const items = yearItems

    this.eventTargets.forEach((item) => item.classList.add("d-none"))
    this.boardTarget.replaceChildren()

    let currentYear = null
    let rail = null

    items.forEach((item) => {
      item.classList.remove("d-none", "is-same-day")
      const itemYear = item.dataset.year
      if (itemYear !== currentYear) {
        currentYear = itemYear
        const group = this.yearTemplateTarget.content.querySelector(".depozen-ui-timeline__year").cloneNode(true)
        group.querySelector("[data-year-label]").textContent = itemYear || "—"
        this.boardTarget.append(group)
        rail = group.querySelector("[data-rail]")
      }

      rail.append(item)
    })

    this.syncCount(yearItems.filter((item) => item.dataset.discreet !== "true").length)
    this.syncMinorInert()
  }

  // Collapsed rows stay in the layout so they can ease open, but they must leave the tab order.
  syncMinorInert() {
    this.eventTargets.forEach((item) => {
      if (item.dataset.discreet !== "true") return
      if (this.showMinor) item.removeAttribute("inert")
      else item.setAttribute("inert", "")
    })
  }

  // Events of the same day keep their rank (guarantee start before an amendment).
  // A minor event slots in by the time it was recorded, except for the cause window.
  compareItems(a, b) {
    const direction = this.sortDescending ? -1 : 1
    const byDate = (a.dataset.pointDate || "").localeCompare(b.dataset.pointDate || "")
    if (byDate !== 0) return byDate * direction

    const aDiscreet = a.dataset.discreet === "true"
    const bDiscreet = b.dataset.discreet === "true"
    if (aDiscreet !== bDiscreet) {
      const byCause = this.compareCause(a, b)
      if (byCause !== 0) return byCause * direction
    }

    const byRank = Number(a.dataset.rank || 0) - Number(b.dataset.rank || 0)
    if (byRank !== 0) return byRank * direction

    const byTime = (a.dataset.occurredAt || "").localeCompare(b.dataset.occurredAt || "")
    if (byTime !== 0) return byTime * direction

    return (a.dataset.eventId || "").localeCompare(b.dataset.eventId || "", undefined, { numeric: true }) * direction
  }

  // Negative when a happened before b. A save logged just after an event is that event's cause.
  compareCause(a, b) {
    const aTime = Date.parse(a.dataset.occurredAt || "")
    const bTime = Date.parse(b.dataset.occurredAt || "")
    if (!Number.isFinite(aTime) || !Number.isFinite(bTime)) return 0

    const aDiscreet = a.dataset.discreet === "true"
    if (aDiscreet === (b.dataset.discreet === "true")) return 0

    const discreetTime = aDiscreet ? aTime : bTime
    const eventTime = aDiscreet ? bTime : aTime
    const loggedAfter = discreetTime - eventTime
    if (loggedAfter >= 0 && loggedAfter <= this.constructor.causeWindowMs) {
      return aDiscreet ? -1 : 1
    }

    if (aTime === bTime) return 0
    return aTime < bTime ? -1 : 1
  }

  syncCount(count) {
    if (!this.hasCountTarget) return

    const template = count === 1 ? this.countTarget.dataset.countOne : this.countTarget.dataset.countOther
    this.countTarget.textContent = (template || "").replace("__COUNT__", String(count))
  }
}
